import { useCallback, useEffect, useRef, useState } from 'react';

import { diagnostics } from '@/utils/diagnostics';

import { useMemoGenerationTrackerState } from './useMemoGenerationTrackerState';

/**
 * Mirrors document summary processing behavior for memo generation/regeneration:
 * show immediate UI feedback after the mutation succeeds, keep the pending state
 * tied to the workflow tracker lifecycle, and refetch once generation finishes.
 */
export function useMemoProcessing(
  memoId: string,
  householdId: string,
  onRefetch: () => void
) {
  const [isGeneratingOverride, setIsGeneratingOverride] = useState(false);
  const prevIsGeneratingRef = useRef(false);

  const memoGenerationTrackerState = useMemoGenerationTrackerState(
    memoId,
    householdId
  );
  const isMemoProcessing = memoGenerationTrackerState?.isInProgress ?? false;

  useEffect(() => {
    if (!isMemoProcessing) {
      setIsGeneratingOverride(false);
    }
  }, [isMemoProcessing]);

  const isGenerating = isMemoProcessing || isGeneratingOverride;

  useEffect(() => {
    const prevIsGenerating = prevIsGeneratingRef.current;
    prevIsGeneratingRef.current = isGenerating;

    if (prevIsGenerating && !isGenerating) {
      diagnostics.debug(
        '[MemoDetails] Memo generation processing completed, refetching',
        {
          memoId,
          householdId,
        }
      );
      onRefetch();
    }
  }, [householdId, isGenerating, memoId, onRefetch]);

  const onStartGenerating = useCallback(() => {
    setIsGeneratingOverride(true);
  }, []);

  return {
    isGenerating,
    onStartGenerating,
  };
}
